import socketInstance from './socketInstance'
import { IGameSettings } from '../data/gameSettings'
import documentVisibility from '../documentVisibility'
import mixpanel from '../mixpanel'

const _pendingEvents: any[] = []
let _isConnected = false
let _hasJoined = false

const emitMessage = (eventType: string, payload: any = {}, withCallback = false) => {
	if ((_isConnected && eventType === 'join') || _hasJoined) {
		return socketInstance.emit(eventType, payload, withCallback)
	} else {
		// if useris not connected, we push new messages to a queue that will be consumed
		// when connection is reestablished
		return new Promise((resolve) => {
			_pendingEvents.push({ eventType, payload, withCallback, resolve })
		})
	}
}

const joinPlayer = (jwt: string, gameSessionUid: string) => {
	return emitMessage('join', {
		jwt,
		gameSessionUid,
	})
}

const pingServer = () => {
	return emitMessage('nearpodPing')
}

const loginStudent = (avatar: string) => {
	return emitMessage('login', {
		avatar,
	})
}

const onGameUpdate = (callback: (data: any) => void) => {
	socketInstance.on('status', callback)
}

const onStudentSubmission = (callback: (data: any) => void) => {
	socketInstance.on('studentSubmission', callback)
}

const onJoined = (callback: (data: any) => void) => {
	socketInstance.on('joined', callback)
}

const onLimitReached = (callback: (data: any) => void) => {
	socketInstance.on('limit_reached', callback)
}

const clearGameUpdate = (_callback: (data: any) => void) => {
	//TODO clear listener
}

const addSimulatedPlayer = () => {
	return emitMessage('addPlayer')
}

const startGame = () => {
	return emitMessage('start')
}

const endGame = () => {
	return emitMessage('end')
}

const resetGame = () => {
	return emitMessage('reset')
}

const submitStudentAnswer = (activityUid: string, selectedAnswersIDs: string[], answeredTime: number) => {
	return emitMessage('submit', {
		activityUid,
		selectedAnswersIDs,
		answeredTime,
	})
}

const connect = (path: string, query: any, callback: () => void) => {
	const unique_track_id = (Date.now() + (Math.random() * 1000)).toString();
	let disconnection_counter = 0;

	socketInstance.connect(path, query)

	socketInstance.on('connect', () => {
		_isConnected = true
		if (callback) {
			callback()
		}
	})

	socketInstance.on('joined', () => {
		_hasJoined = true
	})

	socketInstance.on('disconnect', () => {
		if (!documentVisibility.isHidden()) {
			disconnection_counter++;
			mixpanel.track('TTC socket disconnection', {
				id: unique_track_id,
				count: disconnection_counter,
			}, false)
		}

		_isConnected = false
		_hasJoined = false
	})
}

const emitPendingMessage = async (event: any) => {
	// Wrapping every pending event on a try / catch statement so messages don't get
	// stuck in the queue.
	try {
		const value = await emitMessage(event.eventType, event.payload, event.withCallback)
		if (event.withCallback && event.resolve) {
			event.resolve(value)
		}
	} catch (err) {
		// eslint-disable-next-line no-console
		console.log(err)
	}
}

// This method emits all events that might have been queued if the user got disconnected
// momentarily
const processPendingEvents = async () => {
	if (_isConnected) {
		while (_pendingEvents.length) {
			const event = _pendingEvents.shift()
			// awaiting inside a loop because we want to guarantee order if messages
			// have a callback
			await emitPendingMessage(event)
		}
	}
}

const submitSettings = (settings: IGameSettings) => {
	return emitMessage('settings', settings)
}

const getCurrentNumberOfCorrectAnswersByParticipants = () => {
	return emitMessage('getCurrentNumberOfCorrectAnswersByParticipants', {}, true)
}

const moveToNextActivity = () => {
	return emitMessage('moveToNextActivity')
}

const disconnect = () => {
	return socketInstance.disconnect()
}

const reconnect = () => {
	socketInstance.reconnect()
}

if (documentVisibility.isSupported()) {
	let timeoutId: NodeJS.Timeout
	documentVisibility.onChange(() => {
		if (documentVisibility.isHidden() && _isConnected) {
			// Waiting 5 minutes before disconnecting the user
			// so they don't lose their current question if they go away for a shorter time
			timeoutId = setTimeout(disconnect, 5 * 60000)
		} else if (!documentVisibility.isHidden()) {
			clearTimeout(timeoutId)
			if (!_isConnected) {
				reconnect()
			}
		}
	})
}

export default {
	joinPlayer,
	loginStudent,
	onGameUpdate,
	onStudentSubmission,
	onJoined,
	onLimitReached,
	clearGameUpdate,
	addSimulatedPlayer,
	startGame,
	endGame,
	resetGame,
	pingServer,
	submitStudentAnswer,
	connect,
	processPendingEvents,
	submitSettings,
	disconnect,
	getCurrentNumberOfCorrectAnswersByParticipants,
	moveToNextActivity,
}
